// FILE: src/utils/contacts-ui.js
// Shared contacts utilities + modal
// • Adds categories: Work, School/College, Community, Neighbourhood
// • Normalises relationship → category
// • Groups options in the new category order across all pages

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, onSnapshot, query, where, serverTimestamp } from "firebase/firestore";
import Modal from "../components/ui/Modal";

// ---------- Category config ----------
export const CONTACT_CATEGORIES = [
  "Family",
  "Friends",
  "Work",
  "School/College",
  "Community",
  "Neighbourhood",
  "Other",
];

// Map free-text relationship/category into one of the buckets above
export function normalizeCategory(input) {
  const t = String(input || "").toLowerCase().trim();
  if (!t) return "Other";

  if (/\b(family|parent|mother|father|mum|mom|dad|sister|brother|spouse|husband|wife|partner|child|son|daughter|grand|cousin|aunt|uncle|in[- ]law)\b/.test(t)) return "Family";
  if (/\b(friend|buddy|mate|bestie|pals?)\b/.test(t)) return "Friends";
  if (/\b(work|colleag|co[- ]?worker|office|boss|manager|hr|client|vendor|contractor|company|team)\b/.test(t)) return "Work";
  if (/\b(school|college|university|uni|classmate|teacher|professor|lecturer|alumni|alumn(us|a)?)\b/.test(t)) return "School/College";
  if (/\b(community|club|association|volunteer|charit(y|ies)|church|parish|committee|societ(y|ies))\b/.test(t)) return "Community";
  if (/\b(neighbo(u)?r(hood)?|street|block|estate)\b/.test(t)) return "Neighbourhood";
  return "Other";
}

// ---------- Data shaping ----------
function shapeContact(docSnap) {
  const d = docSnap.data();
  const name =
    (d.name || d.fullName || d.displayName || [d.firstName, d.lastName].filter(Boolean).join(" ")).trim() ||
    d.email ||
    d.phone ||
    "Unnamed Contact";
  const rawCat = d.relationship || d.category || d.type;
  return {
    id: docSnap.id,
    name,
    email: d.email || "",
    phone: d.phone || "",
    category: normalizeCategory(rawCat),
    created: d.created?.toMillis?.() || d.createdAt?.toMillis?.() || 0,
    raw: d,
  };
}

// ---------- Hook: user contacts ----------
export function useUserContacts(user) {
  const uid = user?.uid;
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) return;
    setLoading(true);

    const q1 = query(collection(db, "contacts"), where("uid", "==", uid));
    const q2 = query(collection(db, "contacts"), where("userId", "==", uid)); // legacy

    const un1 = onSnapshot(q1, (snap) => {
      setContacts((prev) => {
        const map = new Map(prev.map((c) => [c.id, c]));
        snap.forEach((d) => map.set(d.id, shapeContact(d)));
        return Array.from(map.values());
      });
    });
    const un2 = onSnapshot(q2, (snap) => {
      setContacts((prev) => {
        const map = new Map(prev.map((c) => [c.id, c]));
        snap.forEach((d) => map.set(d.id, shapeContact(d)));
        return Array.from(map.values());
      });
    });

    return () => {
      try { un1 && un1(); } catch {}
      try { un2 && un2(); } catch {}
    };
  }, [uid]);

  const sorted = useMemo(() => {
    const list = contacts.slice();
    list.sort((a, b) => {
      const ai = CONTACT_CATEGORIES.indexOf(a.category);
      const bi = CONTACT_CATEGORIES.indexOf(b.category);
      if (ai !== bi) return ai - bi;
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [contacts]);

  useEffect(() => {
    if (uid) setLoading(false);
  }, [uid]);

  return { contacts: sorted, loading };
}

// Also export as default so either import style works
export default useUserContacts;

// ---------- Group for selects ----------
export function groupContactsForOptions(contacts) {
  const buckets = Object.fromEntries(CONTACT_CATEGORIES.map((c) => [c, []]));
  (contacts || []).forEach((c) => {
    (buckets[c.category] || (buckets[c.category] = [])).push(c);
  });
  return CONTACT_CATEGORIES.map((c) => ({
    key: c,
    label: `${c} (${buckets[c].length})`,
    items: buckets[c],
  }));
}

// ---------- Add Contact modal ----------
export function AddContactModal({ open, onClose, user }) {
  const defaultCategory = CONTACT_CATEGORIES[0] || "Friends";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    category: defaultCategory,
  });

  useEffect(() => {
    if (open) {
      setErr("");
      setBusy(false);
    }
  }, [open]);

  const save = useCallback(async () => {
    if (!user?.uid) return;
    const name = [f.firstName, f.lastName].filter(Boolean).join(" ").trim() || f.email || f.phone;
    if (!name) {
      setErr("Please enter a name, email or phone");
      return;
    }
    setBusy(true);
    try {
      await addDoc(collection(db, "contacts"), {
        uid: user.uid,
        name,
        firstName: f.firstName,
        lastName: f.lastName,
        email: f.email,
        phone: f.phone,
        relationship: f.category, // store human label; normaliser maps it back
        created: serverTimestamp(),
      });
      onClose && onClose();
      setF({ firstName: "", lastName: "", email: "", phone: "", category: f.category });
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [f, onClose, user?.uid]);

  const formId = "add-contact";

  return (
    
     <Modal open={open} onClose={onClose} title="Add Contact" maxWidth={560}>
      <form
        className="contact-add-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) save();
        }}
      >
        <div className="form-grid form-grid--two">
          <div className="field">
            <label htmlFor={`${formId}-first`}>First name</label>
            <input
              id={`${formId}-first`}
              className="input"
              placeholder="First name"
              value={f.firstName}
              autoComplete="given-name"
              onChange={(e) => setF({ ...f, firstName: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor={`${formId}-last`}>Last name</label>
            <input
              id={`${formId}-last`}
              className="input"
              placeholder="Last name"
              value={f.lastName}
              autoComplete="family-name"
              onChange={(e) => setF({ ...f, lastName: e.target.value })}
            />
          </div>
          </div>

           <div className="form-grid form-grid--two">
          <div className="field">
            <label htmlFor={`${formId}-email`}>Email</label>
            <input
              id={`${formId}-email`}
              className="input"
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor={`${formId}-phone`}>Phone</label>
            <input
              id={`${formId}-phone`}
              className="input"
              type="tel"
              placeholder="Phone"
              autoComplete="tel"
              value={f.phone}
              onChange={(e) => setF({ ...f, phone: e.target.value })}
            />
          </div>
        </div>

          <div className="field">
          <label htmlFor={`${formId}-category`}>Category</label>
          <select
             id={`${formId}-category`}
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })}
          >
            {CONTACT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

          {err ? (
          <div className="empty-state" role="alert" style={{ marginTop: 4 }}>
            {err}
          </div>
         ) : null}

     <div className="form-actions" style={{ marginTop: 12 }}>
          <button type="submit" className="btn btn-main" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" className="btn btn-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Helper: auto-select newly created contact ----------
export function useAddContactAutoSelect(contacts, onCreated) {
  const [open, setOpen] = useState(false);
  const baselineRef = useRef(new Set());

  // Defer opening one frame to avoid the open click being caught by any outside-click close
  const openModal = useCallback(() => {
    if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
      window.requestAnimationFrame(() => setOpen(true));
    } else {
      setTimeout(() => setOpen(true), 0);
    }
  }, []);

  // Capture baseline *when opening*
  useEffect(() => {
    if (open) {
      baselineRef.current = new Set((contacts || []).map((c) => c.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // While open, if a new ID appears, auto-select & close
  useEffect(() => {
    if (!open) return;
    const now = new Set((contacts || []).map((c) => c.id));
    const added = [...now].filter((id) => !baselineRef.current.has(id));
    if (added.length) {
      const byId = new Map((contacts || []).map((c) => [c.id, c]));
      const newest =
        added
          .map((id) => byId.get(id))
          .filter(Boolean)
          .sort((a, b) => (b.created - a.created) || (a.name || "").localeCompare(b.name || ""))[0] || null;
      if (newest && typeof onCreated === "function") onCreated(newest);
      setOpen(false);
    }
  }, [contacts, open, onCreated]);

  return { open, setOpen, openModal };
}
